<template>
  <div class="amount-stepper">
    <label
      v-if="label"
      class="amount-stepper__label"
    >
      {{ label }}
    </label>
    <div class="amount-stepper__row">
      <button
        type="button"
        class="amount-stepper__btn"
        :disabled="!canStepDown || disabled"
        :aria-label="decrementLabel"
        @click="stepDown"
      >
        <i class="fa-regular fa-minus" />
      </button>
      <div class="amount-stepper__input">
        <InputNumber
          :model-value="modelValue"
          :min="min"
          :max="max"
          :show-buttons="false"
          :disabled="disabled"
          :input-class="['amount-stepper__field']"
          @update:model-value="onChange"
        />
        <span class="amount-stepper__suffix">{{ suffix }}</span>
      </div>
      <button
        type="button"
        class="amount-stepper__btn"
        :disabled="!canStepUp || disabled"
        :aria-label="incrementLabel"
        @click="stepUp"
      >
        <i class="fa-regular fa-plus" />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType,
} from 'vue';
import InputNumber from 'primevue/inputnumber';

export default defineComponent({
  name: 'AmountStepper',

  components: { InputNumber },

  props: {
    modelValue: {
      type: Number as PropType<number>,
      required: true,
    },
    /** Predefined step values, e.g. [10, 25, 50, 100, 200, 500]. +/- jumps to the next/previous in this list. */
    steps: {
      type: Array as PropType<number[]>,
      required: true,
    },
    suffix: {
      type: String,
      default: '€',
    },
    label: {
      type: String,
      default: '',
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100000,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    incrementLabel: {
      type: String,
      default: 'Increase amount',
    },
    decrementLabel: {
      type: String,
      default: 'Decrease amount',
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const canStepUp = computed(() => (
      props.modelValue < props.steps[props.steps.length - 1]
    ));
    const canStepDown = computed(() => props.modelValue > 0);

    const stepUp = () => {
      const next = props.steps.find((s) => s > props.modelValue);
      if (next != null) emit('update:modelValue', next);
    };

    const stepDown = () => {
      const prev = [...props.steps].reverse().find((s) => s < props.modelValue);
      emit('update:modelValue', prev ?? 0);
    };

    const onChange = (v: number | null | undefined) => {
      const value = v == null || v < 0 ? 0 : Number(v);
      emit('update:modelValue', value);
    };

    return {
      canStepUp,
      canStepDown,
      stepUp,
      stepDown,
      onChange,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.amount-stepper {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 420px;
  margin: 0 auto;
  text-align: center;
  width: 100%;

  &__label {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
    color: #94a3b8;
    margin-bottom: 10px;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
  }

  &__btn {
    appearance: none;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 1.5px solid #e2e8f0;
    background: #fff;
    color: #334155;
    font-size: 1rem;
    cursor: pointer;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 140ms ease;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

    &:hover:not(:disabled) {
      border-color: $brand-color-primary;
      color: $brand-color-primary;
      background: rgba(148, 200, 64, 0.08);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px -4px rgba(148, 200, 64, 0.35);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &__input {
    position: relative;
    display: flex;
    align-items: stretch;
    flex: 1;
    min-width: 0;
    border-radius: 16px;
    overflow: hidden;
    border: 2px solid #e2e8f0;
    background: #fff;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    transition: border-color 160ms ease, box-shadow 160ms ease;

    &:hover {
      border-color: #cbd5e1;
    }

    &:focus-within {
      border-color: $brand-color-primary;
      box-shadow:
        0 0 0 4px rgba(148, 200, 64, 0.16),
        0 1px 2px rgba(15, 23, 42, 0.04);
    }
  }

  :deep(.p-inputnumber) {
    display: flex;
    width: 100%;
    height: 68px;
    align-items: stretch;
  }

  :deep(.amount-stepper__field) {
    width: 100%;
    flex: 1;
    // Symmetric padding keeps the digits visually centered; the right pad is
    // just wide enough to clear the absolute-positioned suffix below.
    padding: 0 44px !important;
    height: 68px;
    box-sizing: border-box;
    // Font is sized to fit up to 7 chars ("100 000") within the available
    // width at our smallest dialog sizing — keeps the same hero feel without
    // clipping on big amounts.
    font-size: 2rem;
    font-weight: 800;
    color: #0f172a;
    border: none;
    border-radius: 0;
    text-align: center;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    background: transparent;
    margin: 0;

    &:focus {
      outline: none;
      box-shadow: none;
    }
  }

  &__suffix {
    position: absolute;
    right: 18px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 1rem;
    font-weight: 600;
    pointer-events: none;
    white-space: nowrap;
  }
}
</style>
